import React, { useCallback, useState, useEffect } from 'react';

import { ScrollView } from 'devextreme-react';

import LocalizationApi from '../../api/LocalizationApi';

import DataProviderType from '../../classes/consts/DataProviderType';

import DataSourceTypeLabelDto from '../../classes/dtos/localizationmodule/DataSourceTypeLabelDto';

import DataTableReadDto from '../../classes/dtos/pathcommonmodule/DataTableReadDto';
import DataSourceDataTableReadDto from '../../classes/dtos/pathcommonmodule/DataSourceDataTableReadDto';
import PathDataTableReadDto from '../../classes/dtos/pathcommonmodule/PathDataTableReadDto';
import DataProviderReadDto from '../../classes/dtos/pathcommonmodule/DataProviderReadDto';
import DataTableRelationDto from '../../classes/dtos/pathcommonmodule/DataTableRelationDto';

import DataSourceSelectionDto from '../../classes/dtos/datasourcemodule/DataSourceSelectionDto';
import DatabaseSelectionDto from '../../classes/dtos/datastructuremodule/DatabaseSelectionDto';
import TableSelectionDto from '../../classes/dtos/datastructuremodule/TableSelectionDto';

import DataSourceSelection from './DataSourceSelection';
import DatabaseSelection from './DatabaseSelection';
import TableSelection from './TableSelection';
import RelationsSelection from './RelationsSelection';


interface SourceSelectionProps {
    OriginTableId: number | null;
    OriginPathId: number | null;

    Direction: string;
    WithDataTable: boolean;
    TableMapping: DataTableReadDto | null;
    SetTableMapping(value: DataTableReadDto): void;
    DataSourceRefreshed(): void;
    WithCombined?: boolean;
    LinkedDataTables?: DataTableRelationDto[];
}

const SourceSelection: React.FC<SourceSelectionProps> = ({ OriginTableId, OriginPathId, Direction, WithDataTable, TableMapping, SetTableMapping, DataSourceRefreshed, WithCombined = false, LinkedDataTables = [] }) => {

    const defaultDatabaseLabel = 'database';
    const defaultTableLabel = 'table';

    const [dataSourceTypesLabels, setDataSourceTypesLabels] = useState<DataSourceTypeLabelDto[]>([]);

    const [databaseTypeLabel, setDatabaseTypeLabel] = useState<string>(defaultDatabaseLabel);
    const [tableTypeLabel, setTableTypeLabel] = useState<string>(defaultTableLabel);

    useEffect(() => {
        LocalizationApi.GetDataSourceTypesLabelsAsync().then(getDataSourceTypesLabelsResult => {
            if (getDataSourceTypesLabelsResult.IsSuccess()) {

                let labels = getDataSourceTypesLabelsResult.Result.Labels;

                setDataSourceTypesLabels(labels);

                if (TableMapping instanceof DataSourceDataTableReadDto) {
                    setDatabaseTypeLabel(labels.find(e => e.DataSourceType === TableMapping?.DataSourceTypeId && e.Reference === 1)?.Label ?? defaultDatabaseLabel);
                    setTableTypeLabel(labels.find(e => e.DataSourceType === TableMapping?.DataSourceTypeId && e.Reference === 3)?.Label ?? defaultTableLabel);
                }
            }
        })
    }, []);

    const handleSetDataSourceSelected = useCallback((dataSourceSelectionDto: DataSourceSelectionDto) => {

        if (dataSourceSelectionDto.DataProvider !== null) {

            if (dataSourceSelectionDto.DataProvider.Type === DataProviderType.DataSource) {

                let newTableMapping = new DataSourceDataTableReadDto(
                    dataSourceSelectionDto.DataProvider.DataSource!.Id,
                    dataSourceSelectionDto.DataProvider.DataSource!.Name,
                    dataSourceSelectionDto.DataProvider.DataSource!.Type.Id,
                    dataSourceSelectionDto.DataProvider.DataSource!.Type.Name,
                    0,
                    '',
                    0,
                    '');

                if (TableMapping === null || TableMapping instanceof PathDataTableReadDto) {
                    SetTableMapping(newTableMapping);

                    setDatabaseTypeLabel(dataSourceTypesLabels.find(e => e.DataSourceType === newTableMapping?.DataSourceTypeId && e.Reference === 1)?.Label ?? defaultDatabaseLabel);
                    setTableTypeLabel(dataSourceTypesLabels.find(e => e.DataSourceType === newTableMapping?.DataSourceTypeId && e.Reference === 3)?.Label ?? defaultTableLabel);


                } else if (TableMapping instanceof DataSourceDataTableReadDto) {
                    if (TableMapping.DataSourceId !== dataSourceSelectionDto.DataProvider.DataSource!.Id) {
                        SetTableMapping(newTableMapping);

                        setDatabaseTypeLabel(dataSourceTypesLabels.find(e => e.DataSourceType === newTableMapping?.DataSourceTypeId && e.Reference === 1)?.Label ?? defaultDatabaseLabel);
                        setTableTypeLabel(dataSourceTypesLabels.find(e => e.DataSourceType === newTableMapping?.DataSourceTypeId && e.Reference === 3)?.Label ?? defaultTableLabel);
                    }
                }
            }

            if (dataSourceSelectionDto.DataProvider.Type === DataProviderType.PathDataTable) {

                let newTableMapping = new PathDataTableReadDto(
                    dataSourceSelectionDto.DataProvider.PathDataTable!.Id,
                    dataSourceSelectionDto.DataProvider.PathDataTable!.Name);

                if (TableMapping === null || TableMapping instanceof DataSourceDataTableReadDto) {
                    SetTableMapping(newTableMapping);

                    setDatabaseTypeLabel(defaultDatabaseLabel);
                    setTableTypeLabel(defaultTableLabel);


                } else if (TableMapping instanceof PathDataTableReadDto) {
                    if (TableMapping.PathId !== dataSourceSelectionDto.DataProvider.PathDataTable!.Id) {
                        SetTableMapping(newTableMapping);

                        setDatabaseTypeLabel(defaultDatabaseLabel);
                        setTableTypeLabel(defaultTableLabel);
                    }
                }
            }
        }

    }, [SetTableMapping, TableMapping, dataSourceTypesLabels]);

    const handleSetDatabaseSelected = useCallback((databaseSelectionDto: DatabaseSelectionDto) => {

        if (TableMapping !== null && databaseSelectionDto.Database !== null) {

            if (TableMapping instanceof DataSourceDataTableReadDto) {
                if (TableMapping.DatabaseReferenceId !== databaseSelectionDto.Database.Id) {

                    let newTableMapping = new DataSourceDataTableReadDto(
                        TableMapping.DataSourceId,
                        TableMapping.DataSourceLabel,
                        TableMapping.DataSourceTypeId,
                        TableMapping.DataSourceTypeLabel,
                        databaseSelectionDto.Database.Id,
                        databaseSelectionDto.Database.Name,
                        0,
                        '');

                    SetTableMapping(newTableMapping);
                }
            }
        }

    }, [SetTableMapping, TableMapping]);

    const handleSetTableSelected = useCallback((tableSelectionDto: TableSelectionDto) => {

        if (TableMapping !== null && tableSelectionDto.Table !== null) {

            if (TableMapping instanceof DataSourceDataTableReadDto) {
                if (TableMapping.TableReferenceId !== tableSelectionDto.Table.Id) {

                    let newTableMapping = new DataSourceDataTableReadDto(
                        TableMapping.DataSourceId,
                        TableMapping.DataSourceLabel,
                        TableMapping.DataSourceTypeId,
                        TableMapping.DataSourceTypeLabel,
                        TableMapping.DatabaseReferenceId,
                        TableMapping.DatabaseReferenceLabel,
                        tableSelectionDto.Table.Id,
                        tableSelectionDto.Table.Name);

                    SetTableMapping(newTableMapping);
                }
            }
        }

    }, [SetTableMapping, TableMapping]);

    if (dataSourceTypesLabels.length === 0) {
        return (<div></div>);
    }

    let dataProvider = new DataProviderReadDto(TableMapping);

    return (
        <ScrollView style={{ width: '100%', height: 'calc(100vh - 320px)' }}>
            <div style={{ width: '100%', minHeight: 'calc(100vh - 320px)', height: 'auto', padding: '20px', position: 'relative', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'inline-flex' }}>
                <div style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'inline-flex' }}>
                    <DataSourceSelection DataProviderType={dataProvider.Type} DataSourceId={dataProvider.Id ?? 0} DataSourceLabel={dataProvider.Label} SetDataSourceSelected={handleSetDataSourceSelected} Direction={Direction} WithDataTable={WithDataTable} />
                    {dataProvider.WithDatabase && <div style={{ width: 1, height: 32, transformOrigin: '0 0', border: '1px #3D3D46 dotted' }}></div>}
                    {dataProvider.WithDatabase && <DatabaseSelection DataSourceId={dataProvider.DataSourceDataTableRead?.DataSourceId ?? 0} DatabaseReferenceId={dataProvider.DataSourceDataTableRead?.DatabaseReferenceId ?? 0} DatabaseReferenceLabel={dataProvider.DataSourceDataTableRead?.DatabaseReferenceLabel ?? "?????"} DatabaseTypeLabel={databaseTypeLabel} SetDatabaseSelected={handleSetDatabaseSelected} />}
                    {dataProvider.WithTable && <div style={{ width: 1, height: 32, transformOrigin: '0 0', border: '1px #3D3D46 dotted' }}></div>}
                    {dataProvider.WithTable && <TableSelection OriginTableId={OriginTableId} OriginPathId={OriginPathId} DataSourceId={dataProvider.DataSourceDataTableRead?.DataSourceId ?? 0} DatabaseReferenceId={dataProvider.DataSourceDataTableRead?.DatabaseReferenceId ?? 0} TableReferenceId={dataProvider.DataSourceDataTableRead?.TableReferenceId ?? 0} TableReferenceLabel={dataProvider.DataSourceDataTableRead?.TableReferenceLabel ?? "?????"} TableTypeLabel={tableTypeLabel} SetTableSelected={handleSetTableSelected} Direction={Direction} DataSourceRefreshed={DataSourceRefreshed} />}
                    {WithCombined && <RelationsSelection LinkedDataTables={LinkedDataTables} />}
                </div>
            </div>
        </ScrollView>
    );
}

export default SourceSelection;